import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";
import {
  HrAttendance,
  HrDashboard,
  HrDepartment,
  HrDesignation,
  HrDirectoryEmployee,
  HrHoliday,
  HrLeaveBalance,
  HrLeaveBalanceLog,
  HrLeaveRequest,
  HrLeaveType,
  HrPresentOnLeaveRequest,
  HrMyToday,
  HrProfile,
  HrEnrollmentMap,
  HrPunch,
  HrPunchIntegration,
} from "../../types/HrTypes";
import { Shifts } from "../../types/shiftsTypes";

const fail = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.message || fallback;
  }
  return fallback;
};

interface HrState {
  dashboard: HrDashboard | null;
  directory: HrDirectoryEmployee[];
  departments: HrDepartment[];
  designations: HrDesignation[];
  shifts: Shifts[];
  attendance: HrAttendance[];
  punches: HrPunch[];
  myToday: HrMyToday | null;
  leaveTypes: HrLeaveType[];
  leaves: HrLeaveRequest[];
  presentRequests: HrPresentOnLeaveRequest[];
  balances: HrLeaveBalance[];
  holidays: HrHoliday[];
  punchIntegration: HrPunchIntegration | null;
  enrollmentMaps: HrEnrollmentMap[];
  selected: { employee: HrDirectoryEmployee; profile: HrProfile } | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: HrState = {
  dashboard: null,
  directory: [],
  departments: [],
  designations: [],
  shifts: [],
  attendance: [],
  punches: [],
  myToday: null,
  leaveTypes: [],
  leaves: [],
  presentRequests: [],
  balances: [],
  holidays: [],
  punchIntegration: null,
  enrollmentMaps: [],
  selected: null,
  loading: false,
  saving: false,
  error: null,
};

export const fetchHrDashboard = createAsyncThunk(
  "hr/dashboard",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/dashboard");
      return res.data.data as HrDashboard;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load HR dashboard"));
    }
  },
);

export const fetchHrOrg = createAsyncThunk(
  "hr/org",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/org");
      return res.data.data as {
        departments: HrDepartment[];
        designations: HrDesignation[];
        shifts: Shifts[];
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load organization"));
    }
  },
);

export const fetchHrDirectory = createAsyncThunk(
  "hr/directory",
  async (
    params: { search?: string; department_id?: string; status?: string } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/directory", { params });
      return res.data.data as HrDirectoryEmployee[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load directory"));
    }
  },
);

export const fetchHrEmployeeCard = createAsyncThunk(
  "hr/employeeCard",
  async (empId: number, { rejectWithValue }) => {
    try {
      const res = await api.get(`/hr/employees/${empId}`);
      return res.data.data as {
        employee: HrDirectoryEmployee;
        profile: HrProfile;
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load employee"));
    }
  },
);

export const updateHrEmployeeCard = createAsyncThunk(
  "hr/updateEmployee",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const res = await api.post("/hr/employees/update", payload);
      return res.data.data as {
        employee: HrDirectoryEmployee;
        profile: HrProfile;
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to update employee"));
    }
  },
);

export const upsertHrDepartment = createAsyncThunk(
  "hr/upsertDept",
  async (payload: Partial<HrDepartment> & { name: string; code: string }, { rejectWithValue }) => {
    try {
      await api.post("/hr/departments", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save department"));
    }
  },
);

export const deleteHrDepartment = createAsyncThunk(
  "hr/deleteDept",
  async (department_id: number, { rejectWithValue }) => {
    try {
      await api.delete("/hr/departments", { data: { department_id } });
      return department_id;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to delete department"));
    }
  },
);

export const upsertHrDesignation = createAsyncThunk(
  "hr/upsertDesig",
  async (payload: Partial<HrDesignation> & { name: string }, { rejectWithValue }) => {
    try {
      await api.post("/hr/designations", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save designation"));
    }
  },
);

export const deleteHrDesignation = createAsyncThunk(
  "hr/deleteDesig",
  async (designation_id: number, { rejectWithValue }) => {
    try {
      await api.delete("/hr/designations", { data: { designation_id } });
      return designation_id;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to delete designation"));
    }
  },
);

export const fetchHrAttendance = createAsyncThunk(
  "hr/attendance",
  async (
    params: { from?: string; to?: string; emp_id?: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/attendance", { params });
      return res.data.data as HrAttendance[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load attendance"));
    }
  },
);

export const fetchHrPunches = createAsyncThunk(
  "hr/punches",
  async (
    params: { from?: string; to?: string; emp_id?: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/punches", { params });
      return res.data.data as HrPunch[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load punches"));
    }
  },
);

export const markHrAttendance = createAsyncThunk(
  "hr/markAttendance",
  async (
    payload: {
      emp_id: number;
      attendance_date: string;
      status: string;
      notes?: string;
      leave_type_code?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/attendance/mark", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to mark attendance"));
    }
  },
);

export const fetchHrMyToday = createAsyncThunk(
  "hr/myToday",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/me/today");
      return res.data.data as HrMyToday | null;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load today"));
    }
  },
);

export const markHrAttendanceDay = createAsyncThunk(
  "hr/markAttendanceDay",
  async (
    payload: {
      attendance_date: string;
      status: string;
      emp_ids?: number[];
      leave_type_code?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post("/hr/attendance/mark-day", payload);
      return (res.data.message as string) || "Attendance marked";
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to mark attendance for the day"));
    }
  },
);

export const fetchPunchReminderBypass = createAsyncThunk(
  "hr/punchReminderBypass",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/punch-reminder-bypass");
      return res.data.data as {
        active: boolean;
        until: string | null;
        by: string | null;
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load punch reminder bypass"));
    }
  },
);

export const setPunchReminderBypass = createAsyncThunk(
  "hr/setPunchReminderBypass",
  async (enabled: boolean, { rejectWithValue }) => {
    try {
      const res = await api.post("/hr/punch-reminder-bypass", { enabled });
      return res.data.data as {
        active: boolean;
        until: string | null;
        by: string | null;
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to update punch reminder bypass"));
    }
  },
);

export const punchHrSelf = createAsyncThunk(
  "hr/selfPunch",
  async (_: void | undefined, { rejectWithValue }) => {
    try {
      await api.post("/hr/me/punch", { punch_type: "raw" });
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Punch failed"));
    }
  },
);

export const fetchPunchIntegration = createAsyncThunk(
  "hr/punchConfig",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/punch/integration");
      return res.data.data as HrPunchIntegration;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load punch integration"));
    }
  },
);

export const updatePunchIntegration = createAsyncThunk(
  "hr/updatePunchConfig",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await api.post("/hr/punch/integration", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save punch integration"));
    }
  },
);

export const syncPunchIntegration = createAsyncThunk(
  "hr/syncPunch",
  async (payload: { from?: string; to?: string } | undefined, { rejectWithValue }) => {
    try {
      const res = await api.post("/hr/punch/sync", payload || {});
      return (res.data.message as string) || "Sync finished";
    } catch (err) {
      return rejectWithValue(fail(err, "Punch sync failed"));
    }
  },
);

export const fetchEnrollmentMaps = createAsyncThunk(
  "hr/enrollmentMaps",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/punch/enrollment-map");
      return res.data.data as HrEnrollmentMap[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load enrollment map"));
    }
  },
);

export const saveEnrollmentMap = createAsyncThunk(
  "hr/saveEnrollmentMap",
  async (payload: { enrollment_id: string; emp_id: number }, { rejectWithValue }) => {
    try {
      await api.post("/hr/punch/enrollment-map", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save enrollment map"));
    }
  },
);

export const clearEnrollmentMap = createAsyncThunk(
  "hr/clearEnrollmentMap",
  async (enrollment_id: string, { rejectWithValue }) => {
    try {
      await api.post("/hr/punch/enrollment-map/clear", { enrollment_id });
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to clear enrollment map"));
    }
  },
);

export const fetchLeaveTypes = createAsyncThunk(
  "hr/leaveTypes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/leave-types");
      return res.data.data as HrLeaveType[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load leave types"));
    }
  },
);

export const upsertLeaveType = createAsyncThunk(
  "hr/upsertLeaveType",
  async (payload: Partial<HrLeaveType> & { name: string; code: string }, { rejectWithValue }) => {
    try {
      await api.post("/hr/leave-types", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save leave type"));
    }
  },
);

export const fetchHrLeaves = createAsyncThunk(
  "hr/leaves",
  async (
    params: { emp_id?: number; status?: string } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/leaves", { params });
      return res.data.data as HrLeaveRequest[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load leaves"));
    }
  },
);

export const fetchPresentOnLeaveRequests = createAsyncThunk(
  "hr/presentRequests",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/leaves/present-requests");
      return res.data.data as HrPresentOnLeaveRequest[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load present requests"));
    }
  },
);

export const reviewPresentOnLeave = createAsyncThunk(
  "hr/reviewPresentOnLeave",
  async (
    payload: { request_id: number; action: "approved" | "rejected" },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leaves/present-requests/review", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to review present request"));
    }
  },
);

export const fetchLeaveBalances = createAsyncThunk(
  "hr/balances",
  async (
    params: { emp_id?: number; year?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/leave-balances", { params });
      return res.data.data as HrLeaveBalance[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load balances"));
    }
  },
);

export const setHrLeaveBalance = createAsyncThunk(
  "hr/setLeaveBalance",
  async (
    payload: {
      emp_id: number;
      leave_type_id: number;
      allocated?: number;
      remaining?: number;
      used?: number;
      year?: number;
      source?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leave-balances", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save leave count"));
    }
  },
);

export const fetchLeaveBalanceLogs = createAsyncThunk(
  "hr/leaveBalanceLogs",
  async (
    params:
      | {
          emp_id?: number;
          leave_type_id?: number;
          year?: number;
          from?: string;
          to?: string;
          page?: number;
          limit?: number;
        }
      | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/hr/leave-balance-logs", { params });
      return res.data.data as {
        rows: HrLeaveBalanceLog[];
        total: number;
        page: number;
        limit: number;
      };
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load leave balance logs"));
    }
  },
);

export const saveHrOpeningBalances = createAsyncThunk(
  "hr/openingBalances",
  async (
    payload: {
      year: number;
      rows: Array<{
        emp_id: number;
        pl_remaining?: number | string;
        cl_remaining?: number | string;
        hol_remaining?: number | string;
        ul_used?: number | string;
      }>;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leave-balances/opening", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save opening balances"));
    }
  },
);

export const markHrPersonalHoliday = createAsyncThunk(
  "hr/markPersonalHoliday",
  async (
    payload: { emp_id: number; attendance_date: string; half_day?: boolean },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leaves/mark-holiday", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to mark holiday"));
    }
  },
);

export const removeHrLeaveBalance = createAsyncThunk(
  "hr/removeLeaveBalance",
  async (balance_id: number, { rejectWithValue }) => {
    try {
      await api.post("/hr/leave-balances/remove", { balance_id });
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to remove leave type"));
    }
  },
);

export const applyHrLeave = createAsyncThunk(
  "hr/applyLeave",
  async (
    payload: {
      emp_id?: number;
      leave_type_id: number;
      dates: string[];
      from_date?: string;
      to_date?: string;
      reason?: string;
      half_day?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leaves/apply", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to apply leave"));
    }
  },
);

export const grantHrDirectLeave = createAsyncThunk(
  "hr/grantDirectLeave",
  async (
    payload: {
      emp_id: number;
      leave_type_id: number;
      dates: string[];
      from_date?: string;
      to_date?: string;
      reason?: string;
      half_day?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leaves/grant-direct", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to grant leave"));
    }
  },
);

export const reviewHrLeave = createAsyncThunk(
  "hr/reviewLeave",
  async (
    payload: { leave_id: number; action: "approved" | "rejected"; review_note?: string },
    { rejectWithValue },
  ) => {
    try {
      await api.post("/hr/leaves/review", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to review leave"));
    }
  },
);

export const cancelHrLeave = createAsyncThunk(
  "hr/cancelLeave",
  async (leave_id: number, { rejectWithValue }) => {
    try {
      await api.post("/hr/leaves/cancel", { leave_id });
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to cancel leave"));
    }
  },
);

export const fetchHrHolidays = createAsyncThunk(
  "hr/holidays",
  async (year: number | undefined, { rejectWithValue }) => {
    try {
      const res = await api.get("/hr/holidays", { params: { year } });
      return res.data.data as HrHoliday[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load holidays"));
    }
  },
);

export const upsertHrHoliday = createAsyncThunk(
  "hr/upsertHoliday",
  async (payload: Partial<HrHoliday> & { holiday_date: string; name: string }, { rejectWithValue }) => {
    try {
      await api.post("/hr/holidays", payload);
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to save holiday"));
    }
  },
);

export const deleteHrHoliday = createAsyncThunk(
  "hr/deleteHoliday",
  async (holiday_id: number, { rejectWithValue }) => {
    try {
      await api.delete("/hr/holidays", { data: { holiday_id } });
      return true;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to delete holiday"));
    }
  },
);

const hrSlice = createSlice({
  name: "hr",
  initialState,
  reducers: {
    clearHrError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const pending = (state: HrState) => {
      state.loading = true;
      state.error = null;
    };
    const saving = (state: HrState) => {
      state.saving = true;
      state.error = null;
    };
    const rejected = (state: HrState, action: { payload?: unknown }) => {
      state.loading = false;
      state.saving = false;
      state.error = (action.payload as string) || "HR request failed";
    };

    builder
      .addCase(fetchHrDashboard.pending, pending)
      .addCase(fetchHrDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchHrOrg.fulfilled, (state, action) => {
        state.departments = action.payload.departments;
        state.designations = action.payload.designations;
        state.shifts = action.payload.shifts;
      })
      .addCase(fetchHrDirectory.pending, pending)
      .addCase(fetchHrDirectory.fulfilled, (state, action) => {
        state.loading = false;
        state.directory = action.payload;
      })
      .addCase(fetchHrEmployeeCard.fulfilled, (state, action) => {
        state.selected = action.payload;
      })
      .addCase(updateHrEmployeeCard.pending, saving)
      .addCase(updateHrEmployeeCard.fulfilled, (state, action) => {
        state.saving = false;
        state.selected = action.payload;
      })
      .addCase(fetchHrAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.attendance = action.payload;
      })
      .addCase(fetchHrPunches.fulfilled, (state, action) => {
        state.punches = action.payload;
      })
      .addCase(fetchHrMyToday.fulfilled, (state, action) => {
        state.myToday = action.payload;
      })
      .addCase(fetchPunchIntegration.fulfilled, (state, action) => {
        state.punchIntegration = action.payload;
      })
      .addCase(fetchEnrollmentMaps.fulfilled, (state, action) => {
        state.enrollmentMaps = action.payload;
      })
      .addCase(fetchLeaveTypes.fulfilled, (state, action) => {
        state.leaveTypes = action.payload;
      })
      .addCase(fetchHrLeaves.fulfilled, (state, action) => {
        state.loading = false;
        state.leaves = action.payload;
      })
      .addCase(fetchPresentOnLeaveRequests.fulfilled, (state, action) => {
        state.presentRequests = action.payload;
      })
      .addCase(fetchLeaveBalances.fulfilled, (state, action) => {
        state.balances = action.payload;
      })
      .addCase(fetchHrHolidays.fulfilled, (state, action) => {
        state.holidays = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith("hr/") && action.type.endsWith("/rejected"),
        rejected,
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("hr/") &&
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("dashboard") &&
          !action.type.includes("directory") &&
          !action.type.includes("attendance") &&
          !action.type.includes("leaves"),
        (state) => {
          state.saving = false;
          state.loading = false;
        },
      );
  },
});

export const { clearHrError } = hrSlice.actions;
export default hrSlice.reducer;
